import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  FormControlLabel, 
  Switch, 
  Paper,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  COOKIE_KEY, 
  POLICY_VERSION, 
  CookieConsent, 
  getCookieConsent 
} from '../utils/cookieConsent';
import { loadAnalytics, revokeAnalyticsConsent } from '../utils/analytics';

interface CookieSettingsProps {
  showTitle?: boolean;
}

const CookieSettings: React.FC<CookieSettingsProps> = ({ showTitle = true }) => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState('');
  
  // Load current settings on component mount
  useEffect(() => {
    const consent = getCookieConsent();
    if (consent) {
      setAnalyticsEnabled(consent.acceptedAnalytics);
    }
  }, []);
  
  const handleAnalyticsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnalyticsEnabled(event.target.checked);
  };
  
  const handleSaveSettings = () => {
    const consentData: CookieConsent = {
      version: POLICY_VERSION,
      timestamp: Date.now(),
      acceptedAnalytics: analyticsEnabled,
    };
    
    // Store consent in localStorage
    localStorage.setItem(COOKIE_KEY, JSON.stringify(consentData));
    
    // Set cookie for server-side access
    const ttl = 180 * 24 * 60 * 60; // 180 days in seconds
    document.cookie = `cookie_consent=${analyticsEnabled ? 'analytics' : 'essential'}; Path=/; Max-Age=${ttl}; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
    
    // Load or unload analytics based on settings
    if (analyticsEnabled) {
      loadAnalytics();
      setMessage('Analytics cookies enabled.');
    } else {
      revokeAnalyticsConsent();
      setMessage('Analytics cookies disabled.');
    }
    
    // Show success message
    setSnackbarOpen(true);
  };
  
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      {showTitle && (
        <>
          <Typography variant="h5" gutterBottom>
            Cookie Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}
      
      <Typography paragraph>
        Customize your cookie preferences below. Some cookies are essential for the basic functioning of our website and cannot be disabled.
      </Typography>
      
      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Switch 
              checked={analyticsEnabled} 
              onChange={handleAnalyticsChange}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" fontWeight="medium">
                Analytics Cookies
              </Typography>
              <Typography variant="body2" color="text.secondary">
                These cookies help us understand how visitors interact with our website by collecting and reporting anonymous information via Google Analytics and other analytics services.
              </Typography>
            </Box>
          }
          sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            m: 0, 
            mb: 2 
          }}
        />
        
        <FormControlLabel
          control={
            <Switch 
              checked={true} 
              disabled
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" fontWeight="medium">
                Essential Cookies
              </Typography>
              <Typography variant="body2" color="text.secondary">
                These cookies are necessary for the website to function properly and cannot be disabled.
              </Typography>
            </Box>
          }
          sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            m: 0, 
            mb: 2 
          }}
        />
      </Box>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSaveSettings}
        >
          Save Preferences
        </Button>
      </Box>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CookieSettings; 